import { Car } from './car';
import { fetchWithTimeout } from './fetchWithTimeout';
import { UserGroup } from './user-group';

export type User = {
    userId?: string;
    account?: string;
    password?: string;
    userName?: string;
    tel?: string | null;
    mobile?: string | null;
    email?: string | null;
    address?: string | null;
    lineId?: string | null;
    lineName?: string | null;
    picture?: string | null;
    createBy?: string;
    createDate?: Date;
    updateBy?: string;
    updateDate?: Date;
    userGroups?: UserGroup[];
    cars?: Car[];
};

type CountResponseData = {
    statusCode: number;
    message: string;
    success: boolean;
    data: number;
};

function count(token?: string) {
    return fetchWithTimeout('user/count', {
        method: 'GET',
    })
        .then((res) => res.json())
        .then((data: CountResponseData) => data)
        .catch((error) => {
            throw error;
        })
        .finally(() => {});
}

type CreateAdminRequestData = User & {};

type CreateAdminResponseData = {
    statusCode: number;
    message: string;
    success: boolean;
    data?: User;
};

function createAdmin(data: CreateAdminRequestData) {
    return fetchWithTimeout('user/createAdmin', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
    })
        .then((res) => res.json())
        .then((data: CreateAdminResponseData) => data)
        .catch((error) => {
            throw error;
        })
        .finally(() => {});
}

type CreateRequestData = User & {};

type CreateResponseData = {
    statusCode: number;
    message: string;
    success: boolean;
    data?: User;
};

function create(data: CreateRequestData) {
    return fetchWithTimeout('user/create', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
    })
        .then((res) => res.json())
        .then((data: CreateResponseData) => data)
        .catch((error) => {
            throw error;
        })
        .finally(() => {});
}

type CreateBulkResponseData = {
    statusCode: number;
    message: string;
    success: boolean;
    data?: User[];
};

function createBulk(data: CreateRequestData[]) {
    return fetchWithTimeout('user/createBulk', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
    })
        .then((res) => res.json())
        .then((data: CreateBulkResponseData) => data)
        .catch((error) => {
            throw error;
        })
        .finally(() => {});
}

type GetRequestData = User & {
    select?: (keyof User)[];
    relations?: {
        [key in keyof Pick<User, 'userGroups' | 'cars'>]?: boolean;
    };
};

type GetResponseData = {
    statusCode: number;
    message: string;
    success: boolean;
    data?: User[];
};

function get(data: GetRequestData) {
    return fetchWithTimeout('user/get', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
    })
        .then((res) => res.json())
        .then((data: GetResponseData) => data)
        .catch((error) => {
            throw error;
        })
        .finally(() => {});
}

type UpdateRequestData = User & {
    userId: string;
};

type UpdateResponseData = {
    statusCode: number;
    message: string;
    success: boolean;
    data?: User;
};

function update(data: UpdateRequestData) {
    return fetchWithTimeout('user/update', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
    })
        .then((res) => res.json())
        .then((data: UpdateResponseData) => data)
        .catch((error) => {
            throw error;
        })
        .finally(() => {});
}

type UpdateBulkResponseData = {
    statusCode: number;
    message: string;
    success: boolean;
    data?: User[];
};

function updateBulk(data: UpdateRequestData[]) {
    return fetchWithTimeout('user/updateBulk', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
    })
        .then((res) => res.json())
        .then((data: UpdateBulkResponseData) => data)
        .catch((error) => {
            throw error;
        })
        .finally(() => {});
}

type RemoveRequestData = User & { userId: string };

type RemoveResponseData = {
    statusCode: number;
    message: string;
    success: boolean;
    data?: {};
};

function remove(data: RemoveRequestData) {
    return fetchWithTimeout('user/remove', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
    })
        .then((res) => res.json())
        .then((data: RemoveResponseData) => data)
        .catch((error) => {
            throw error;
        })
        .finally(() => {});
}

function removeBulk(data: RemoveRequestData[]) {
    return fetchWithTimeout('user/removeBulk', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
    })
        .then((res) => res.json())
        .then((data: RemoveResponseData) => data)
        .catch((error) => {
            throw error;
        })
        .finally(() => {});
}

type SendLineMessageRequestData = { lineId: string; messages: { type: 'text'; text: string }[] };

type SendLineMessageResponseData = {
    statusCode: number;
    message: string;
    success: boolean;
    data?: {};
};

function sendLineMessage(data: SendLineMessageRequestData) {
    return fetchWithTimeout('user/sendLineMessage', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
    })
        .then((res) => res.json())
        .then((data: SendLineMessageResponseData) => data)
        .catch((error) => {
            throw error;
        })
        .finally(() => {});
}

export default { count, createAdmin, create, createBulk, get, update, updateBulk, remove, removeBulk, sendLineMessage };
